import React, { FC, useEffect, useState } from 'react';
import styled from 'astroturf/react';
import ModalBase from 'js/components/modals/Modal';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Heading as HeadingBase } from 'js/design_system/Heading';
import { Colors } from 'js/design_system/Color';
import CloseIcon from 'js/design_system/icons/close.svg';
import { createUserBulkFollowing } from 'js/social/api/user_bulk_following_api';
import { Suggestion } from 'js/social/shared/types';
import { Spinner } from 'js/social/shared/CreateSocialItem/Spinner';
import BulkFollowSuggestion, { FollowSuggestion } from './BulkFollowSuggestion';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import {
  CardType,
  getListModalHeader,
  getListModalImpressionSource,
  getFollowingSource,
} from './bulk_follow_modal_helper';
import _SpinnerContainer from "BulkFollowListModal-SpinnerContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/big_cards/BulkFollowListModal.tsx?styleId=SpinnerContainer";
import _Modal from "BulkFollowListModal-Modal.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/big_cards/BulkFollowListModal.tsx?styleId=Modal";
import _CloseButton from "BulkFollowListModal-CloseButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/big_cards/BulkFollowListModal.tsx?styleId=CloseButton";
import _Heading from "BulkFollowListModal-Heading.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/big_cards/BulkFollowListModal.tsx?styleId=Heading";
import _SuggestionList from "BulkFollowListModal-SuggestionList.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/big_cards/BulkFollowListModal.tsx?styleId=SuggestionList";
import _UpdateButtonContainer from "BulkFollowListModal-UpdateButtonContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/big_cards/BulkFollowListModal.tsx?styleId=UpdateButtonContainer";
import _Cta from "BulkFollowListModal-Cta.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/big_cards/BulkFollowListModal.tsx?styleId=Cta";


const SpinnerContainer = /*#__PURE__*/styled("div", null, {
  displayName: "SpinnerContainer",
  styles: _SpinnerContainer
});

const Modal = /*#__PURE__*/styled(ModalBase, null, {
  displayName: "Modal",
  styles: _Modal
});

const CloseButton = /*#__PURE__*/styled(TextButton, null, {
  displayName: "CloseButton",
  styles: _CloseButton
});

const Heading = /*#__PURE__*/styled(HeadingBase, null, {
  displayName: "Heading",
  styles: _Heading
});

const SuggestionList = /*#__PURE__*/styled("div", null, {
  displayName: "SuggestionList",
  styles: _SuggestionList
});

const UpdateButtonContainer = /*#__PURE__*/styled("div", null, {
  displayName: "UpdateButtonContainer",
  styles: _UpdateButtonContainer
});

const Cta = /*#__PURE__*/styled(Button, null, {
  displayName: "Cta",
  styles: _Cta
});

type Props = {
  open: boolean;
  asyncFollowSuggSource?: () => Promise<{ suggestions: Suggestion[] }>;
  initialFollowSuggestions: FollowSuggestion[];
  cardType: CardType;
  onClose: (followingsAdded: boolean) => void;
  topic?: {
    name: string;
    score?: number;
  };
};

const BulkFollowListModal: FC<Props> = ({
  open,
  asyncFollowSuggSource,
  initialFollowSuggestions,
  cardType,
  onClose,
  topic,
}) => {
  const [followSuggestions, setFollowSuggestions] = useState<
    FollowSuggestion[]
  >(initialFollowSuggestions);
  const [loading, setLoading] = useState(false);
  const [followingsAdded, setFollowingsAdded] = useState(false);

  useEffect(() => {
    if (asyncFollowSuggSource) {
      const fetchData = async () => {
        setLoading(true);
        const response = await asyncFollowSuggSource();
        const respFollowSuggestions = response.suggestions.map(
          (sugg: Suggestion) => {
            const suggReason =
              sugg.sources && sugg.metadata
                ? {
                    sources: sugg.sources,
                    metadata: sugg.metadata,
                  }
                : undefined;
            return {
              id: sugg.user.id,
              displayName: sugg.user.display_name,
              photoUrl: sugg.user.photo,
              universityName: sugg.user.department?.university?.name,
              departmentName: sugg.user.department?.name,
              suggestionReason: suggReason,
              following: sugg.user.followed,
            };
          },
        );
        setFollowSuggestions(respFollowSuggestions);
        setLoading(false);
      };
      fetchData();
    }
  }, [asyncFollowSuggSource]);

  const followAll = async () => {
    const suggestionDetailMap: {
      [key: number]: { [key: string]: string | number };
    } = {};

    followSuggestions.forEach((suggestion) => {
      suggestionDetailMap[suggestion.id] =
        suggestion.suggestionReason !== undefined
          ? { suggestion_reason: suggestion.suggestionReason.sources[0] }
          : {
              topic: topic?.name ?? '',
              topic_score: topic?.score ?? 0,
              individual_follow: 'false',
            };
    });

    return await createUserBulkFollowing(
      followSuggestions.map((followSugg) => followSugg.id),
      getFollowingSource(cardType),
      false,
      suggestionDetailMap,
    );
  };

  return (
    <>
      {open && (
        <Modal onClose={() => onClose(followingsAdded)}>
          <Heading size="sm">
            {getListModalHeader(cardType, topic?.name)}
          </Heading>

          <CloseButton
            aria-label="Close"
            onClick={() => onClose(followingsAdded)}
          >
            <CloseIcon color={Colors.GrayDark} />
          </CloseButton>
          {loading ? (
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          ) : (
            <>
              <SuggestionList>
                {followSuggestions.map((followSugg, index) => (
                  <BulkFollowSuggestion
                    key={`modal-follow-suggestion-${followSugg.id}`}
                    index={index}
                    onFollow={() => setFollowingsAdded(true)}
                    followableUser={followSugg}
                    following={followSugg.following}
                    followingSource={getFollowingSource(cardType)}
                    impressionSource={getListModalImpressionSource(
                      cardType,
                      'follow',
                    )}
                    topic={topic}
                  />
                ))}
              </SuggestionList>

              <UpdateButtonContainer>
                <ImpressionWatcher
                  source={getListModalImpressionSource(cardType, 'follow_A')}
                >
                  <Cta
                    onClick={async () => {
                      await followAll();
                      setFollowingsAdded(true);
                      onClose(true);
                    }}
                  >
                    Follow all
                  </Cta>
                </ImpressionWatcher>
              </UpdateButtonContainer>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default BulkFollowListModal;
